import { type AddonRow, type Locale, type Tr, adminApi } from '@/lib/api';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

const LOCALES: Locale[] = ['pt', 'en', 'fr', 'es', 'de'];

function emptyTr(): Tr {
  return { pt: '', en: '', fr: '', es: '', de: '' };
}

interface Props {
  addon: AddonRow | null;
  onClose: () => void;
  onSaved: () => void;
}

export function AddonForm({ addon, onClose, onSaved }: Props) {
  const isNew = addon === null;
  const [key, setKey] = useState(addon?.key ?? '');
  const [name, setName] = useState<Tr>(addon?.name ?? emptyTr());
  const [description, setDescription] = useState<Tr>(addon?.description ?? emptyTr());
  const [eurDefault, setEur] = useState<number>(addon?.eurDefault ?? 0);
  const [cveDefault, setCve] = useState<number>(addon?.cveDefault ?? 0);
  const [active, setActive] = useState<boolean>(addon?.active ?? false);
  const [activeLocale, setActiveLocale] = useState<Locale>('pt');
  const [error, setError] = useState<string | null>(null);

  const nameDone = LOCALES.filter((l) => name[l]?.trim().length > 0).length;
  const descDone = LOCALES.filter((l) => description[l]?.trim().length > 0).length;

  const save = useMutation<unknown, Error, void>({
    mutationFn: async () => {
      const body = { key, name, description, eurDefault, cveDefault, active };
      if (isNew) return adminApi.createAddon(body);
      return adminApi.patchAddon(addon!.id, body);
    },
    onSuccess: onSaved,
    onError: (err) => {
      const msg = (err as Error).message;
      setError(
        msg.includes('incomplete_translations')
          ? 'Para activar o extra preencha as 5 línguas (Nome + Descrição).'
          : msg.includes('key_taken')
            ? 'Chave já existente — escolha outra.'
            : msg,
      );
    },
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(12,35,34,0.55)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
        style={{
          background: '#fff',
          width: 540,
          padding: 28,
          border: '1px solid var(--lv-line)',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div className="display" style={{ fontSize: 22 }}>
          {isNew ? 'NOVO EXTRA' : 'EDITAR EXTRA'}
        </div>
        <div className="label" style={{ color: 'var(--lv-brand)', marginTop: 4 }}>
          Catálogo de add-ons
        </div>

        <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
          <Field label="Chave (snake_case, ex: child_seat)">
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              style={input}
              required
              maxLength={60}
              readOnly={!isNew}
            />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="EUR (default)">
              <input
                type="number"
                step="0.5"
                min="0"
                value={eurDefault}
                onChange={(e) => setEur(Number(e.target.value))}
                style={input}
                required
              />
            </Field>
            <Field label="CVE (default)">
              <input
                type="number"
                step="100"
                min="0"
                value={cveDefault}
                onChange={(e) => setCve(Number(e.target.value))}
                style={input}
                required
              />
            </Field>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, marginTop: 20, borderBottom: '1px solid var(--lv-line)' }}>
          {LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setActiveLocale(l)}
              style={{
                background: activeLocale === l ? 'var(--lv-brand)' : 'transparent',
                color: activeLocale === l ? '#fff' : '#222',
                border: 0,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {l}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 11, color: 'var(--lv-gray-500)' }}>
            Nome {nameDone}/5 · Descr. {descDone}/5
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
          <Field label={`Nome (${activeLocale.toUpperCase()})`}>
            <input
              type="text"
              value={name[activeLocale]}
              onChange={(e) => setName({ ...name, [activeLocale]: e.target.value })}
              style={input}
            />
          </Field>
          <Field label={`Descrição (${activeLocale.toUpperCase()})`}>
            <textarea
              value={description[activeLocale]}
              onChange={(e) => setDescription({ ...description, [activeLocale]: e.target.value })}
              rows={3}
              style={input}
            />
          </Field>
        </div>

        <label style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Activo (visível no checkout)
        </label>

        {error && (
          <div
            style={{
              marginTop: 16,
              padding: '8px 12px',
              background: '#fce4e4',
              color: 'var(--lv-err)',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={btnGhost}>
            Cancelar
          </button>
          <button type="submit" disabled={save.isPending} style={btnPrimary}>
            {save.isPending ? '…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span
        style={{
          display: 'block',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--lv-gray-500)',
        }}
      >
        {label}
      </span>
      <span style={{ marginTop: 4, display: 'block' }}>{children}</span>
    </label>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--lv-line)',
  background: '#fff',
  fontSize: 13,
};
const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid var(--lv-line)',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
